import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import { toggleModal, toggleNavBar } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { updateExtraByName } from 'apps/auth/actions/extraAction';
import WSChat from 'apps/auth/components/chat/WSChat';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import FraudBlockDisclaimer from 'apps/auth/components/common/FraudMonitoring/FraudBlockDisclaimer';
import FraudBlockInfoModal from 'apps/auth/components/common/FraudMonitoring/FraudBlockInfoModal';
import FraudHardBlockInfoModal from 'apps/auth/components/common/FraudMonitoring/FraudHardBlockInfoModal';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import CloudFlareModal from 'apps/auth/components/common/Modal/CloudFlareModal';
import Popup from 'apps/auth/components/common/Popup/Popup';
import SnackBar from 'apps/auth/components/common/SnackBar/SnackBar';
import WelcomePage from 'apps/auth/components/history/send/WelcomePage';
import AuthPromoBanner from 'apps/auth/components/utils/AuthPromoBanner';
import MessageBodyService from 'apps/auth/components/utils/MessageBodyService';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import ModalWithTimer from 'apps/auth/components/utils/ModalWithTimer';
import Spinner from 'apps/auth/components/utils/Spinner/Spinner';
import { useSuccessPage, useWelcomePage } from 'apps/auth/components/utils/utils';
import { FRAUD_BLOCK_DISCLAIMER_MODAL, FRAUD_BLOCK_INFO_MODAL } from 'apps/auth/constants/ComponentNames';
import { HARD_BLOCK, JWT, USER_COUNTRY_NAME } from 'apps/auth/constants/Constants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { findErrorCodeAndMessage, showErrorType } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import type { RootState } from 'apps/auth/redux/store';
import Cookies from 'universal-cookie';

import { MainColumn } from './MainColumn/MainColumn';
import { StubPage } from './StubPage';

interface ContentProps {
    isShowSpinner?: boolean;
}

type ModalNewHandle = {
    open: (content?: React.ReactNode | null) => void;
    close: () => void;
};

const shouldForwardContentProp = (prop: string) => !['isShowSpinner'].includes(prop);

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const Content = styled.div.withConfig({ shouldForwardProp: shouldForwardContentProp })<ContentProps>`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    pointer-events: ${(props) => (props.isShowSpinner ? 'none' : null)};
    opacity: ${(props) => (props.isShowSpinner ? '0.3' : null)};

    & > form {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    & > div {
        filter: ${(props) => (props.isShowSpinner ? 'blur(2px)' : null)};
    }
`;

const cookies = new Cookies();

type BaseLayoutOwnProps = {
    showPromoBanner?: boolean;
    loading?: boolean;
    dataTestId?: string;
    stateOptions?: { isGA?: boolean };
    children?: React.ReactNode;
};

type StateProps = {
    serverErrors?: ServerErrors;
    messageBodyError?: MessageBodyError;
    modalComponent?: React.ReactNode;
    isShowModal?: boolean;
    isShowModalWithTimer?: boolean;
    isShowCloudFlareModal?: boolean;
    userCountryId?: string | number;
    showFrozenListDisclaimer?: boolean;
    showPreparedPs3MigrationScreen?: boolean;
    paymentStatus?: PaymentStatus;
    serverErrorCode?: ServerErrorCode;
    showMigrationChat?: boolean;
    cheque?: ChequeError;
    isShowSpinner?: boolean;
};

const actions = {
    toggleModal,
    toggleChat,
    toggleNavBar,
    updateExtraByName,
};

type DispatchProps = typeof actions;

type BaseLayoutProps = BaseLayoutOwnProps & StateProps & DispatchProps;

const isVoid = (value: unknown): boolean => value === null || value === undefined || value === 'null';

type FindErrorArgs = Parameters<typeof findErrorCodeAndMessage>;
type MessageBodyError = FindErrorArgs[0];
type ServerErrors = FindErrorArgs[1];
type ChequeError = FindErrorArgs[2];
type PaymentStatus = FindErrorArgs[3];
type ServerErrorCode = FindErrorArgs[4];

type BaseLayoutMessages = {
    successTitle?: string;
    contactSupport?: string;
    gotIt?: string;
    preparingBetaTitle?: string;
    preparingBetaSubtitle?: string;
};

type ExtraState = {
    gaServerNotAvailable?: boolean;
    showFrozenDisclaimer?: boolean;
    showPreparedPs3MigrationScreen?: boolean;
    payment_status?: PaymentStatus;
    serverErrorCode?: ServerErrorCode;
    showMigrationChat?: boolean;
};

type AuthState = {
    jwt?: string | null;
    userCountryName?: string | null;
    countryId?: string | number;
};

const BaseLayoutComponent: React.FC<BaseLayoutProps> = (props) => {
    const modalRef = useRef<ModalNewHandle | null>(null);
    const [modal, setModal] = useState(FRAUD_BLOCK_INFO_MODAL);
    const { successTitle } = usePageMessages<BaseLayoutMessages>() ?? {};
    const [showSuccess] = useSuccessPage(props) as [boolean];
    const [showWelcomePage, toggleWelcomePage] = useWelcomePage(props) as [boolean, (value: boolean) => void];
    const { gaServerNotAvailable } = useExtra<ExtraState>() ?? {};
    const { jwt, userCountryName } = useAuth<AuthState>() ?? {};
    const messages = usePageMessages<BaseLayoutMessages>() ?? {};

    const isUsaUser = props.userCountryId === USA;

    useEffect(() => {
        if (isVoid(jwt)) {
            cookies.remove(JWT, { path: '/' });
        } else if (jwt) {
            cookies.set(JWT, jwt, { path: '/' });
        }

        if (isVoid(userCountryName)) {
            cookies.remove(USER_COUNTRY_NAME, { path: '/' });
        } else if (userCountryName) {
            cookies.set(USER_COUNTRY_NAME, userCountryName, { path: '/' });
        }
    }, [jwt, userCountryName]);

    const handleToggleModal = (errorType?: unknown) => {
        const error = findErrorCodeAndMessage(props.messageBodyError, props.serverErrors, props.cheque, props.paymentStatus, props.serverErrorCode);
        if (errorType === HARD_BLOCK) {
            modalRef.current?.open(<FraudHardBlockInfoModal error={error} modalRef={modalRef} />);
        } else if (modal === FRAUD_BLOCK_INFO_MODAL) {
            modalRef.current?.open(
                <FraudBlockInfoModal modalRef={modalRef} setModal={setModal} errorType={errorType} error={error} isUsaUser={Boolean(isUsaUser)} />,
            );
        } else if (modal === FRAUD_BLOCK_DISCLAIMER_MODAL) {
            modalRef.current?.open(
                <FraudBlockDisclaimer modalRef={modalRef} setModal={setModal} errorType={errorType} error={error} isUsaUser={Boolean(isUsaUser)} />,
            );
        }
    };

    useEffect(() => {
        const errorType = showErrorType(props.messageBodyError, props.serverErrors, props.cheque, props.paymentStatus, props.serverErrorCode);
        if (errorType || props.showFrozenListDisclaimer) {
            handleToggleModal(errorType);
        } else if (props.showPreparedPs3MigrationScreen) {
            togglePs3MigrationModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.messageBodyError,
        props.serverErrors,
        props.cheque,
        modal,
        props.showFrozenListDisclaimer,
        props.showPreparedPs3MigrationScreen,
        props.paymentStatus,
        props.serverErrorCode,
    ]);

    const togglePs3MigrationModal = () => {
        props.toggleModal(
            true,
            <ModalComponent isVerticalCentered canCloseModal={false}>
                <InfoPage
                    insideModalComponent
                    iconName="clock-80"
                    iconColor="primary"
                    primaryButtonCaption={props.showMigrationChat ? messages?.contactSupport : messages?.gotIt}
                    headerText={messages?.preparingBetaTitle}
                    secondaryText={<DangerouslyHtml dangerouslyHtml={messages?.preparingBetaSubtitle} />}
                    primaryButtonOnClick={(event) => {
                        event.preventDefault();
                        if (props.showMigrationChat) {
                            props.toggleChat(true);
                        } else {
                            props.toggleModal(false);
                        }
                    }}
                />
            </ModalComponent>,
        );
    };

    const getRenderComponent = () => {
        if (gaServerNotAvailable && props?.stateOptions?.isGA) {
            return (
                <MainColumn>
                    <StubPage />
                </MainColumn>
            );
        }
        if (!props.loading) {
            return props.children;
        }
        return null;
    };

    if (showWelcomePage) {
        return <WelcomePage hideWelcomePage={() => toggleWelcomePage(false)} />;
    }
    if (showSuccess) {
        return <InfoPage animatedIconSuccess mainText={successTitle} />;
    }

    return (
        <>
            {props.showPromoBanner && <AuthPromoBanner />}
            <Wrapper data-testid={props.dataTestId}>
                <Content isShowSpinner={props.isShowSpinner}>
                    <SnackBar />
                    {getRenderComponent()}
                    {props.isShowModal && props.modalComponent}
                    {props.isShowModalWithTimer && <ModalWithTimer />}
                    {props.isShowCloudFlareModal && <CloudFlareModal />}
                    <Popup />
                    <MessageBodyService />
                    <WSChat />
                </Content>
                <Spinner />
            </Wrapper>
            <ModalNew ref={modalRef} />
        </>
    );
};

const mapStateToProps = ({ app, auth, extra, history }: RootState): StateProps => {
    const appState = app as {
        errors?: ServerErrors;
        messageBody?: MessageBodyError;
        modalComponent?: React.ReactNode;
        isShowModal?: boolean;
        isShowModalWithTimer?: boolean;
        cloudFlareModal?: { isShow?: boolean } | null;
        isShowSpinner?: boolean;
    };
    const authState = auth as AuthState;
    const extraState = extra as ExtraState;
    const historyState = history as { cheque?: { cheque?: ChequeError } };
    return {
        serverErrors: appState.errors,
        messageBodyError: appState.messageBody,
        modalComponent: appState.modalComponent,
        isShowModal: appState.isShowModal,
        isShowModalWithTimer: appState.isShowModalWithTimer,
        isShowCloudFlareModal: appState.cloudFlareModal?.isShow,
        userCountryId: authState.countryId,
        showFrozenListDisclaimer: extraState.showFrozenDisclaimer,
        showPreparedPs3MigrationScreen: extraState.showPreparedPs3MigrationScreen,
        paymentStatus: extraState.payment_status,
        serverErrorCode: extraState.serverErrorCode,
        showMigrationChat: extraState.showMigrationChat,
        cheque: historyState?.cheque?.cheque,
        isShowSpinner: appState.isShowSpinner,
    };
};

export default connect(mapStateToProps, actions)(BaseLayoutComponent as React.ComponentType<any>);
